import { useEffect, useMemo, useState } from 'react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
  Button,
  Input,
  Textarea,
  MultiSelect,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import { useUpdateJourneyExplanation, type CustomerJourney } from '@/api/customer-journey';
import { useProductGroupOptions } from '@/api/product-groups';
import { errorMessage } from './sale-process-error';
import { type DealForm, seedDealForm, toDealPayload } from './deal-info-form-utils';
import { DateInput } from '@/components/date-input';
import { NumericInput } from '@/components/numeric-input';

/**
 * Slide-over to edit a journey's deal info (`customer_journey_explaination`).
 * PUTs the merged explanation via `useUpdateJourneyExplanation`. Budget is sent
 * as a number; `expected_date` as a local `YYYY-MM-DD` (no UTC shift). Only the
 * Phase-1 fields legacy exposes are editable — other embedded keys are
 * preserved by spreading the existing explanation.
 */
export function EditDealInfoDrawer({
  customerId,
  journey,
  open,
  onOpenChange,
}: {
  customerId: number;
  journey: CustomerJourney;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const ex = journey.customer_journey_explaination;
  const [form, setForm] = useState<DealForm>(() => seedDealForm(ex));
  const mutation = useUpdateJourneyExplanation(customerId, journey.customer_journey_id);
  const { data: productGroups = [] } = useProductGroupOptions();

  // Options come from the real product-groups catalog; merge any already-selected
  // values so a saved interest still shows even if the catalog hasn't loaded.
  const serviceOptions = useMemo(() => {
    const byValue = new Map(productGroups.map((o) => [o.value, o]));
    for (const v of form.service_interestings) if (!byValue.has(v)) byValue.set(v, { value: v, label: v });
    return Array.from(byValue.values());
  }, [productGroups, form.service_interestings]);

  // Reseed from the journey each time the drawer opens.
  useEffect(() => {
    if (open) {
      setForm(seedDealForm(ex));
      mutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, journey.customer_journey_id]);

  const set =
    <K extends keyof DealForm>(key: K) =>
    (value: DealForm[K]) =>
      setForm((f) => ({ ...f, [key]: value }));

  const submit = () => {
    mutation.mutate(toDealPayload(ex, form), {
      onSuccess: () => {
        toast.success('Deal info updated');
        onOpenChange(false);
      },
      onError: (err) => toast.error('Update failed', { description: errorMessage(err) }),
    });
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent side="right" size="md">
        <DrawerHeader>
          <DrawerTitle>Edit deal information</DrawerTitle>
          <DrawerDescription>Update the deal context for this journey.</DrawerDescription>
        </DrawerHeader>

        <DrawerBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          {mutation.isError ? (
            <Alert tone="danger" title="Could not save">{errorMessage(mutation.error)}</Alert>
          ) : null}

          <Field label="Interested services">
            {() => (
              <MultiSelect
                placeholder="Select services"
                options={serviceOptions}
                value={form.service_interestings}
                onChange={set('service_interestings')}
              />
            )}
          </Field>
          <Field label="Decision maker">
            {(p) => (
              <Input
                {...p}
                value={form.influencer_explaination}
                onChange={(e) => set('influencer_explaination')(e.target.value)}
                placeholder="Who signs off on this deal"
              />
            )}
          </Field>
          <Field label="Budget (₫)">
            {(p) => (
              <NumericInput
                {...p}
                value={form.budget_explaination}
                onChange={(e) => set('budget_explaination')(e.target.value)}
                placeholder="0"
              />
            )}
          </Field>
          <Field label="Expected close date">
            {(p) => (
              <DateInput
                {...p}
                value={form.expected_date}
                onChange={(e) => set('expected_date')(e.target.value)}
              />
            )}
          </Field>
          <Field label="Provider">
            {(p) => (
              <Input
                {...p}
                value={form.provider_explaination}
                onChange={(e) => set('provider_explaination')(e.target.value)}
                placeholder="Current / competing provider"
              />
            )}
          </Field>
          <Field label="Problem">
            {(p) => (
              <Textarea
                {...p}
                rows={3}
                value={form.problem_explaination}
                onChange={(e) => set('problem_explaination')(e.target.value)}
                placeholder="Pain point this deal solves"
              />
            )}
          </Field>
          <Field label="Expectation">
            {(p) => (
              <Textarea
                {...p}
                rows={3}
                value={form.expected_explaination}
                onChange={(e) => set('expected_explaination')(e.target.value)}
                placeholder="Desired outcome"
              />
            )}
          </Field>
        </DrawerBody>

        <DrawerFooter style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--vn-space-2)' }}>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button onClick={submit} loading={mutation.isPending}>
            Save changes
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}

export default EditDealInfoDrawer;
